import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FileText, Search, Loader2, Download, ArrowLeft,
  File, FileImage, FileVideo, FileAudio, FileArchive, BookOpen
} from 'lucide-react';
import { studentPanelAPI } from '../../services/api';

const MATERIAL_TYPES = [
  { value: '', label: 'Barchasi' },
  { value: 'lecture', label: "Ma'ruza" },
  { value: 'practice', label: 'Amaliyot' },
  { value: 'lab', label: 'Laboratoriya' },
  { value: 'test', label: 'Test' },
  { value: 'other', label: 'Boshqa' }
];

const getFileIcon = (extension) => {
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const videoExts = ['mp4', 'avi', 'mov', 'mkv', 'webm'];
  const audioExts = ['mp3', 'wav', 'ogg', 'flac'];
  const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz'];
  
  if (imageExts.includes(extension)) return FileImage;
  if (videoExts.includes(extension)) return FileVideo;
  if (audioExts.includes(extension)) return FileAudio;
  if (archiveExts.includes(extension)) return FileArchive;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const StudentMaterials = () => {
  const { subjectId } = useParams();
  const [subject, setSubject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchMaterials();
  }, [subjectId, filterType]);

  const fetchMaterials = async () => {
    try {
      const response = await studentPanelAPI.getSubjectMaterials(subjectId, filterType);
      setSubject(response.data.subject);
      setMaterials(response.data.materials || []);
    } catch (error) {
      console.error('Materiallarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMaterials = materials.filter(m => 
    m.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <Link to="/student/subjects" className="back-link">
            <ArrowLeft size={20} />
            Fanlar
          </Link>
          <h1><BookOpen size={28} /> {subject?.name}</h1>
          <p>{materials.length} ta material</p>
        </div>
      </header>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Materialni qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          {MATERIAL_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Materials List */}
      {filteredMaterials.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>Materiallar topilmadi</p>
        </div>
      ) : (
        <div className="materials-grid student-materials-grid">
          {filteredMaterials.map(material => {
            const FileIcon = getFileIcon(material.file_extension);
            return (
              <div key={material.id} className="material-card student">
                <div className="material-icon">
                  <FileIcon size={32} />
                  <span className="file-ext">.{material.file_extension}</span>
                </div>
                <div className="material-content">
                  <h3>{material.title}</h3>
                  <p className="material-teacher">
                    {material.teacher_name}
                  </p>
                  {material.description && (
                    <p className="material-desc">{material.description}</p>
                  )}
                  <div className="material-meta">
                    <span className="badge badge-info">{material.type_display}</span>
                    <span className="file-size">{formatFileSize(material.file_size)}</span>
                  </div>
                </div>
                <div className="material-actions">
                  <a 
                    href={material.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                    title="Yuklab olish"
                  >
                    <Download size={16} />
                    Yuklab olish
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentMaterials;
